import { useState, useCallback, useEffect, useMemo } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { connect } from 'react-redux';
import { passwordStrength } from 'check-password-strength';
import { appFormatText, DATE_FORMAT, DATE_FORMAT_TIME, getStatusColor } from 'helpers/common';
import { changePassword, submitAccountDeletionRequest } from "store/services/authService";
import { updateProfile_ } from "store/services/profileService";
import { Formik } from "formik";
import validation from "./validation";
import { CONFIRM_POPUP, SUCCESS_MESSAGE } from "common/constantMessage";
import { useShowLoader } from "hooks/useShowLoader";
import { cancelDataExportRequest, createExportRequest, downloadExportData, getExportRequestList } from "store/services/accountSettingService";
import useRelativeTime from "hooks/useRelativeTime";
import AccountDeleteConfirmationPopup from "./Components/Modals/AccountDeleteConfirmation";
import { authLogout } from "store/actions/auth";
import ExportWizardPopup from "./Components/Modals/ExportWizardPopup";
import AppTooltip from "components/shared/CommonTooltip";
import moment from "moment";
import { EXPORT_DATA_EXPIRATION_DAYS } from "common/constants";

const AccountInfo = ({ dispatch, userInfo, submitting, exportRequests, remainingExportRequests, maxExportRequests}) => {
  const [step, setStep] = useState("1");
  const [sending, setSending] = useState(false);
  const [exportStatusCheckedAt, setExportStatusCheckedAt] = useState(false);
  const [showAccDeleteConfirmPopup, setShowAccDeleteConfirmPopup] = useState(false);
  const [showExportPopup, setShowExportPopup] = useState(false);

  const relativeTime = useRelativeTime(exportStatusCheckedAt, 20000);

  const {isExportInProgress, isAccountBackedUp} = useMemo(() => {
    let output = {
      isExportInProgress: false,
      isAccountBackedUp: false,
    }
    if(!exportRequests?.length) {
      return output;
    }

    output.isExportInProgress = exportRequests.some(request => ["pending", "in_progress"].includes(request.status));
    output.isAccountBackedUp = exportRequests.some(request => request.status === "success");
    return output;
  }, [exportRequests]);

  const showLoader = useShowLoader();

  const [fields, setfields] = useState({
    currentPassword: "",
    email: "",
  })

  const initialValues = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  }

  useEffect(() => {
    getExportRequests();
  }, [])

  const submitData_ = async (values, { setSubmitting, resetForm }) => {
    try {
      setSubmitting(true);
      values = JSON.stringify(values)
      let response = await dispatch(changePassword(values));
      if (response?.success === true) {
        SUCCESS_MESSAGE("Password updated successfully")
      }
      resetForm();
      setStep(1)
    } catch (err) {
      console.log(err, "==============================");
      setSubmitting(false);
    }
  };

  //handle changes
  const handleChange = useCallback(
    name => evt => {
      setfields(prevState => ({
        ...prevState,
        [name]: evt.target.value,
      }))
    },
    []
  )

  const submitProfileData_ = async (evt) => {
    try {
      evt.preventDefault();
      const fieldsJSON = JSON.stringify(fields);
      setSending(true)
      await dispatch(updateProfile_(fieldsJSON));
      setSending(false)
      setStep(1)
    } catch (err) {
      console.log(err, "==============================");
      setSending(false);
    }
  };

  const getExportRequests = async () => {
    try {
      showLoader(true);
      await dispatch(getExportRequestList());
      setExportStatusCheckedAt(new Date());
      showLoader(false);
    } catch (err) {
      console.log("Error getting export request status", err);
      showLoader(false);
    }
  }

  const addExportRequest = async (handleClose, clients, exportType) => {
    try {
      showLoader(true);
      let payload = {
        exportAllClients: clients?.length ? "no" : "yes",
        include_client_list : exportType === "CSV_PDF" ? "yes" : "no",
        clients
      }

      await createExportRequest(payload);
      await getExportRequests();
      showLoader(false);
      typeof handleClose === "function" && handleClose();
    } catch (err) {
      console.log(err, "==============================");
      showLoader(false);
    }
  }

  const cancelExportRequest = async (requstId) => {
    try {
      let confirm = await CONFIRM_POPUP();
      if(!confirm) {
        return;
      }

      showLoader(true);
      let response = await cancelDataExportRequest(requstId);
      await getExportRequests();
      SUCCESS_MESSAGE(response?.message || "Export request canceled successfully");

      showLoader(false);
    } catch (err) {
      console.error('Error cancelling the export request', err);
      showLoader(false);
    }
  }

  const handleDownloadExport = async (exportId) => {
    try {
      showLoader(true);
      const newTab = window.open('', '_blank');
      newTab.document.writeln('<p>Please wait...</p>');
      try {
        const response = await downloadExportData(exportId);
        if (response?.body) {
          newTab.location.href = response?.body;
          setTimeout(() => newTab.close(), 5000)
        } else {
          newTab.document.writeln('<p>Failed to load.</p>');
        }
      } catch (error) {
        console.error('Error fetching the URL:', error);
        newTab.document.writeln('<p>Error loading the URL.</p>');
      }
      showLoader(false);
    } catch (err) {
      console.log(err, "==============================");
      showLoader(false);
    }
  }

  const handleAccountDeletion = async (values) => {
    try {
      let confirmed = await CONFIRM_POPUP();
      if (!confirmed) {
        return;
      }

      showLoader(true);
      let payload = {
        reason: values?.reason,
      }
      const response = await submitAccountDeletionRequest(payload);
      SUCCESS_MESSAGE(response?.message || "Your account deletion request has been submitted.");
      showLoader(false);
      dispatch(authLogout());
    } catch (err) {
      console.error("Error submitting account deletion request", err);
      showLoader(false);
    }
  }

  const openAccDelConfirmPopup = () => {
    setShowAccDeleteConfirmPopup(true);
  }

  const closeAccountDeleteConfirmPopup = () => {
    setShowAccDeleteConfirmPopup(false);
  }

  const openExportPopup = () => {
    setShowExportPopup(true);
  }

  const closeExportPopup = () => {
    setShowExportPopup(false);
  }

  return (
    <>
      <ExportWizardPopup
        dispatch={dispatch}
        showPopup={showExportPopup}
        closePopup={closeExportPopup}
        userDetail={userInfo}
        addExportRequest={addExportRequest}
      />
      <AccountDeleteConfirmationPopup
        showPopup={showAccDeleteConfirmPopup}
        closePopup={closeAccountDeleteConfirmPopup}
        userDetail={userInfo}
        handleSubmit={handleAccountDeletion}
      />
      {step == "1" ? (
        <>
          <div className="top py-2">
            <h3 className="m-0 py-1 fw-bold">Account Information</h3>
          </div>
          <div className="TabBody py-3">
            <div className="info">
              <div className="py-2">
                <p className="text-bold m-0 text-dark fw-bold capitalize">
                  Name
                </p>
                {userInfo && (
                  <p className="tex-muted m-0">{`${userInfo?.first_name} ${userInfo?.last_name !== null ? userInfo?.last_name : ''}`}</p>
                )}
              </div>
              <div className="py-2">
                <p className="text-bold m-0 text-dark fw-bold capitalize">
                  Login Email
                </p>
                <p className="tex-muted m-0">{userInfo?.email}</p>
              </div>
              <div className="py-2">
                <p className="text-bold m-0 text-dark fw-bold capitalize">
                  Password Last Updated On
                </p>
                <p className="tex-muted m-0"> {DATE_FORMAT_TIME(userInfo?.last_password_updated !== null ? userInfo?.last_password_updated : userInfo?.created_at)}</p>
              </div>
              <div className="py-2">
                <p className="text-bold m-0 text-dark fw-bold capitalize">
                  Account ID
                </p>
                <p className="tex-muted m-0">{userInfo?.uuid}</p>
              </div>
              <div className="py-2">
                <p className="text-bold m-0 text-dark fw-bold capitalize">
                  Practitioner Name
                </p>
                <p className="tex-muted m-0">{userInfo?.display_name}</p>
              </div>
              <div className="py-2">
                <Link
                  onClick={() => setStep("2")}
                  to=""
                  className=" fw-sbold capitalize underline"
                >
                  <i>Update log in email or password</i>
                </Link>
              </div>
              <div className="py-2">
                <Link
                  onClick={() => setStep("3")}
                  to=""
                  className=" fw-sbold capitalize underline"
                >
                  <i>Export your data</i>
                </Link>
              </div>
              <div className="py-2">
                <Link
                  onClick={() => setStep("4")}
                  to=""
                  className="fw-sbold capitalize underline text-danger"
                >
                  <i>Delete Account</i>
                </Link>
              </div>
            </div>
          </div>
        </>
      ) : step == "2" ? (
        <>
          <div className="top py-2">
            <h3 className="m-0 py-1 fw-bold">Edit Log In Information</h3>
          </div>
          <div className="TabBody py-3">
            <div className="info">
              <div className="py-2">
                <p className="text-bold m-0 text-dark fw-bold capitalize">
                  Update Password
                </p>
                <p className="tex-muted m-0">
                  Your password was last updated on {DATE_FORMAT_TIME(userInfo?.last_password_updated !== null ? userInfo?.last_password_updated : userInfo?.created_at)} To
                  update your password please enter in your current password and
                  then your new password below.
                </p>
              </div>
              <div className="py-2">
                <Formik
                  initialValues={initialValues}
                  enableReinitialize
                  validationSchema={validation}
                  onSubmit={submitData_}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                  }) => {
                    return (
                      <Form onSubmit={handleSubmit} autoComplete="off">
                        <Row className="py-2">
                          <Col lg="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label fw-sbold theme-clr m-0"
                            >
                              Current Password
                            </label>
                            <input
                              type="password"
                              className={`form-control ${errors.currentPassword && 'is-invalid'}`}
                              name="currentPassword"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Current Password"
                              value={values.currentPassword}
                            />
                          </Col>
                          {errors.currentPassword && touched.currentPassword && (
                            // <Col lg="6" className="my-2">
                            <p className="m-0 formText errorMessage">
                              {errors.currentPassword}
                            </p>
                            // </Col>
                          )}
                        </Row>
                        <Row className="py-2">
                          <Col lg="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label fw-sbold theme-clr m-0"
                            >
                              New Password
                            </label>
                            <input
                              type="password"
                              className={`form-control ${errors.newPassword && 'is-invalid'}`}
                              name="newPassword"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="New Password"
                              value={values.newPassword}
                            />
                            {values?.newPassword && (
                              <p className="m-0 py-1 password-strength">Password Strength: <span className={`strength strength-${passwordStrength(values.newPassword).id}`}>{passwordStrength(values.newPassword)?.value}</span></p>
                            )}
                            {errors.newPassword && touched.newPassword && (
                              <p className="m-0 formText errorMessage">
                                {errors.newPassword}
                              </p>
                            )}
                          </Col>

                          <Col lg="6" className="my-2">
                            <label
                              htmlFor=""
                              className="form-label fw-sbold theme-clr m-0"
                            >
                              Re-enter New Password
                            </label>
                            <input
                              type="password"
                              className={`form-control ${errors.confirmPassword && 'is-invalid'}`}
                              name="confirmPassword"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="New Password"
                              value={values.confirmPassword}
                            />
                            {errors.confirmPassword && touched.confirmPassword && (
                              <p className="m-0 formText errorMessage">
                                {errors.confirmPassword}
                              </p>
                            )}
                          </Col>
                          <Col lg="12" className="my-2 btnWrp">
                            <Button
                              // onClick={() => setStep("1")}
                              type="submit"
                              disabled={submitting}
                              className="d-flex align-items-center justify-conten-center commonBtn btnSm"
                            >
                              {isSubmitting ? "Wait..." : "Submit"}
                            </Button>
                          </Col>
                        </Row>
                      </Form>
                    )
                  }}
                </Formik>

              </div>
              <div className="py-2">
                <p className="text-bold m-0 text-dark fw-bold capitalize">
                  Update Log In Email
                </p>
                <p className="tex-muted m-0">
                  Your current log in email is{" "}
                  <Link to="" className="underline">
                    {userInfo?.email}
                  </Link>{" "}
                  To update your log in email please enter in your email along
                  with your current password.
                </p>
              </div>
              <div className="py-2">
                <Form>
                  <Row className="py-2">
                    <Col lg="6" className="py-2">
                      <label htmlFor="" className="form-label m-0 fw-sbold">
                        Email
                      </label>
                      <input
                        type="email"
                        placeholder="Email"
                        className="form-control"
                        name="email"
                        onChange={handleChange("email")}
                        value={fields.email}
                      />
                    </Col>
                    <Col lg="6" className="py-2">
                      <label htmlFor="" className="form-label m-0 fw-sbold">
                        Current Password
                      </label>
                      <input
                        type="password"
                        placeholder="Current Password"
                        className="form-control"
                        name="currentPassword"
                        onChange={handleChange("currentPassword")}
                        value={fields.currentPassword}
                      />
                    </Col>
                    <Col lg="12" className="my-2 btnWrp">
                      <Button
                        // onClick={() => setStep("1")}
                        type="submit"
                        onClick={submitProfileData_}
                        className="d-flex align-items-center justify-content-center commonBtn btnSm"
                      >
                        {sending ? 'Wait...' : 'Update Log in Email'}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </div>
            </div>
            <div className="py-2">
              <Link
                onClick={() => setStep("1")}
                to=""
                className=" fw-sbold capitalize underline"
              >
                <i>Go Back</i>
              </Link>
            </div>
          </div>
        </>
      ) : step == "3" ? (
        <>
          <div className="top py-2">
            <div className="d-flex justify-content-between align-items-center">
              <h3 className="m-0 py-1 fw-bold">Export data</h3>
              {exportRequests?.length > 0 && (
                (remainingExportRequests <= 0) ? (
                  <AppTooltip
                    text={"You have no remaining export requests for this month."}
                  >
                    <Button
                      className="d-flex align-items-center justify-conten-center commonBtn btnSm me-4 pointer-not-allowed"
                    >
                      Request Data Export
                    </Button>
                  </AppTooltip>
                ):(
                  <Button
                    className="d-flex align-items-center justify-conten-center commonBtn btnSm me-4"
                    onClick={openExportPopup}
                  >
                    Request Data Export
                  </Button>
                )
              )}
            </div>
          </div>
          <div className="TabBody py-3">
            <div className="info">
              {(!exportRequests?.length) ? (
                <>
                  <div className="py-2">
                    <p className="">
                      You can request an export of your data from our platform. This process may take some time, depending on the size of your data. You will receive an email notification when your export is ready for download.
                    </p>
                    <p>
                      Please note that you can request a data export up to <span className="fw-bold">{maxExportRequests} times per month</span>.
                    </p>
                  </div>
                  <div className="py-2">
                    <Button
                      className="d-flex align-items-center justify-conten-center commonBtn btnSm"
                      onClick={openExportPopup}
                    >
                      Request Data Export
                    </Button>
                  </div>
                </>
              ) : exportRequests?.length > 0 && (
                <>
                  <div className="py-2">
                    <p className="">
                      Below is the list of all export requests made. You can view the status of each request and check for any completed, pending, or failed exports. Exporting the data may take some time, depending on the size of your data. You will receive an email notification once your export is complete. The exported data will be available for {EXPORT_DATA_EXPIRATION_DAYS} days from the date of request.
                    </p>
                    <p className="">
                      If you'd like to create a new export request, use the "Request Data Export" button at the top of the page. Please note that you can request a data export up to <span className="fw-bold">{maxExportRequests} times per month</span>.
                    </p>
                    <p>
                      You have <span className="fw-bold">{remainingExportRequests}</span> export request{remainingExportRequests !== 1 ? "s" : ""} remaining this month.
                    </p>
                  </div>
                  <Row>
                    {exportRequests.map((request, index) => {
                      let requestStatus = request?.status !== "completed" ? request?.status : "in_progress";
                      return (
                        <Col lg="8" className="commonCardBox my-2" key={index}>
                          <div className="d-flex justify-content-between py-2">
                            <div>
                              <div className="pb-2">
                                <p className="m-0">
                                  <span className="fw-bold">Requested on:</span> {DATE_FORMAT_TIME(request?.created_at)}
                                </p>
                              </div>
                              <div className="pb-2">
                                <p className="m-0">
                                  <span className="fw-bold">Status:</span>{" "}
                                  <span style={{ color: getStatusColor(requestStatus) || "inherit" }}>{appFormatText(requestStatus)}</span>
                                </p>
                              </div>
                              {requestStatus === "success" && (
                                <div className="pb-2">
                                  <p className="m-0">
                                    <span className="fw-bold">Expires on:</span>{" "}
                                    {DATE_FORMAT(moment(request?.created_at).add(EXPORT_DATA_EXPIRATION_DAYS, "days").toDate())}
                                  </p>
                                </div>
                              )}
                              {(relativeTime && ["pending", "in_progress", "completed"].includes(requestStatus)) && (
                                <p className="text-muted m-0"><i>Last checked: {relativeTime}</i></p>
                              )}
                            </div>
                            <div>
                              {requestStatus === "success" ? (
                                <AppTooltip
                                  text={"Download Exported Data"}
                                >
                                  <Button
                                    variant="transparent"
                                    className="p-0"
                                    onClick={() => handleDownloadExport(request?.id)}
                                  >
                                    <i class="fa fa-download" aria-hidden="true"></i>
                                  </Button>
                                </AppTooltip>
                              ) : ["pending", "in_progress", "completed"].includes(requestStatus) ? (
                                <>
                                  <AppTooltip
                                    text={"Refresh Status"}
                                  >
                                    <Button
                                      variant="transparent"
                                      className="p-0 me-2"
                                      onClick={getExportRequests}
                                    >
                                      <i class="fa fa-refresh" aria-hidden="true" style={{ fontSize: "15px" }}></i>
                                    </Button>
                                  </AppTooltip>
                                  <AppTooltip
                                    text={"Cancel Request"}
                                  >
                                    <Button
                                      variant="transparent"
                                      className="p-0"
                                      onClick={() => cancelExportRequest(request.id)}
                                    >
                                      <i class="fa fa-times-circle-o" aria-hidden="true"></i>
                                    </Button>
                                  </AppTooltip>
                                </>
                              ) : (
                                <>
                                </>
                              )}
                            </div>
                          </div>
                        </Col>
                      )
                    })}
                  </Row>
                </>
              )}
            </div>
            <div className="py-2">
              <Link
                onClick={() => setStep("1")}
                to=""
                className=" fw-sbold capitalize underline"
              >
                <i>Go Back</i>
              </Link>
            </div>
          </div>
        </>
      ) : step == "4" ? (
        <>
          <div className="top pt-2">
            <h3 className="m-0 py-1 fw-bold">Account Deletion</h3>
          </div>
          <div className="TabBody py-2">
            <div className="info">
              {isExportInProgress ? (
                <>
                  <div className="py-2">
                    <p className="m-0">
                      Your data export is currently in progress. Please wait for the export to complete before proceeding with account deletion. You will receive an email notification once the export is ready. After downloading your data, you can proceed with account deletion.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <p className="">
                    We're sorry to see you go! Before you proceed with deleting your account, we highly recommend exporting your data from the platform. Once your account is deleted, all your data, including saved records and preferences, will be permanently removed and cannot be recovered.
                  </p>
                  <div className="py-2">
                    <h5>Export Your Data</h5>
                    <p className="m-0">
                      To ensure you have a copy of your important information:
                    </p>
                    <ul>
                      <li>Navigate to the <Link onClick={() => setStep("3")}>Data Export</Link> section in your account information.</li>
                      <li>Follow the instructions to download your data.</li>
                      <li>Verify the exported file before proceeding with deletion.</li>
                    </ul>
                  </div>
                  <div className="py-2">
                    {!isAccountBackedUp ? (
                      <div className="d-flex gap-10">
                        <Button
                          className="d-flex align-items-center justify-conten-center commonBtn btnSm"
                          onClick={() => setStep("3")}
                        >
                          Export Data
                        </Button>
                        <Button
                          variant="danger"
                          className="d-flex align-items-center justify-conten-center btnSm"
                          onClick={openAccDelConfirmPopup}
                        >
                          Proceed Without Backup
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Button
                          variant="danger"
                          className="d-flex align-items-center justify-conten-center btnSm"
                          onClick={openAccDelConfirmPopup}
                        >
                          Delete My Account
                        </Button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="py-2">
              <Link
                onClick={() => setStep("1")}
                to=""
                className=" fw-sbold capitalize underline"
              >
                <i>Go Back</i>
              </Link>
            </div>
          </div>
        </>
      ) : (
        ""
      )}
    </>
  );
};
const mapStateToPros = (state) => {
  return {
    userInfo: state.Profile.user,
    exportRequests: state.AccountSetting.dataExport.exportRequests,
    remainingExportRequests: state.AccountSetting.dataExport.remainingExportRequests,
    maxExportRequests: state.AccountSetting.dataExport.maxExportRequests,
  }
};

function mapDispatchToProps(dispatch) {
  return { dispatch };
}

export default connect(
  mapStateToPros,
  mapDispatchToProps
)(AccountInfo);
